"use client";

import { ReactNode } from "react";

import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";

import { cn, formatIsoStringDate } from "@/lib/utils";
import { t } from "@/lib/localization";

import { useGetLomdaV3 } from "@/features/storage/sheets/lomdaV3Sheet/hooks/useGetLomdaV3";
import { GetFilesResponseType } from "../../hooks/useGetFiles";
import LomdaV3Thumbnail from "./lomdaV3Thumbnail";

interface LomdaV3CardProps {
    file: GetFilesResponseType["data"][0];
    onClick?: () => void;
    options?: {
        component: ReactNode;
        positionClassName?: string;
    };
    selected?: boolean;
}

export default function LomdaV3Card({
    file,
    onClick,
    options,
    selected,
}: LomdaV3CardProps) {
    const badgeText =
        file.detailForBadge <= 1
            ? t("storage.lomda-v3-card.num-of-pages-badge.single")
            : file.detailForBadge +
              " " +
              t("storage.lomda-v3-card.num-of-pages-badge.multiple");

    const title = file.name || t("storage.no-name", undefined, undefined);
    const subtitle = `${t("storage.lomda-v3", undefined, undefined)} · ${formatIsoStringDate(file.date)}`;

    const { data } = useGetLomdaV3(true, file.id);

    return (
        <Card
            onClick={onClick}
            className={cn(
                "relative group cursor-pointer hover:bg-accent transition shadow-md flex flex-col",
                selected && "border-primary"
            )}
        >
            {options && (
                <div
                    className={cn(
                        "absolute z-10 flex",
                        options.positionClassName || "top-2 left-2"
                    )}
                >
                    {options.component}
                </div>
            )}

            <div className="relative aspect-video rounded-t-md border-b overflow-hidden bg-muted">
                <LomdaV3Thumbnail
                    lomda={data?.data}
                    lomdaId={file.id}
                    lomdaName={file.name ?? ""}
                />

                <Badge className="absolute bottom-2 left-2 font-normal px-2">
                    {badgeText}
                </Badge>
            </div>

            <footer className="flex flex-row gap-2 px-3 py-4">
                <div className="flex flex-col truncate gap-2">
                    <h3 className="font-medium truncate" title={title}>
                        {title}
                    </h3>
                    <p className="text-xs text-muted-foreground">{subtitle}</p>
                </div>
            </footer>
        </Card>
    );
}
